import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { InferType, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { t } from "i18next";
import { tv } from "tailwind-variants";
import { H3, P } from "@/components/Typography";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { usePostKlaviyoPrivateKey } from "@/requests/hooks/collect.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-duotone-svg-icons";
import { Tooltip } from "@/components/Tooltip";

const styles = tv({
  slots: {
    titleAndDescriptionContainer: "mb-20",
    title: "mb-10 font-bold",
    buttonContainer: "my-45",
  },
});

declare interface IProps {
  klaviyoApiKey?: string;
  isKlaviyoApiKeyPending: boolean;
}

const KlaviyoApiKeyForm = ({
  klaviyoApiKey,
  isKlaviyoApiKeyPending,
}: IProps) => {
  const { titleAndDescriptionContainer, title, buttonContainer } = styles();

  let lastKlaviyoApiKeyUpdate: string;

  const formSchema = object({
    klaviyoApiKey: string()
      .required(t("settings.integration.klaviyoKey.blankPrivateKey"))
      .test(
        "Valid Klaviyo API",
        t("settings.integration.klaviyoKey.invalidPrivateKey"),
        value => value.startsWith("pk_") || value.startsWith("PK_"),
      ),
  });

  type IKlaviyoApiKeyForm = InferType<typeof formSchema>;

  const {
    mutate: postKlaviyoPrivateKey,
    isPending: isPostKlaviyoPrivateKeyPending,
    isSuccess: isPostKlaviyoPrivateKeySuccess,
  } = usePostKlaviyoPrivateKey();

  const {
    getValues,
    watch,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<IKlaviyoApiKeyForm>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      klaviyoApiKey,
    },
  });

  const handleOnSubmitPostKlaviyoApiKey = ({
    klaviyoApiKey,
  }: IKlaviyoApiKeyForm) => {
    postKlaviyoPrivateKey(klaviyoApiKey);
  };

  useEffect(() => {
    if (isPostKlaviyoPrivateKeySuccess) {
      lastKlaviyoApiKeyUpdate = getValues("klaviyoApiKey");
      toast.success(t("settings.integration.klaviyoKey.success"));
    }
  }, [isPostKlaviyoPrivateKeySuccess]);

  const renderTrailingSlotTooltip = () => (
    <Tooltip content={t("settings.integration.klaviyoKey.tooltip")}>
      <FontAwesomeIcon icon={faInfoCircle} size="xl" className="text-gray" />
    </Tooltip>
  );

  const renderTitleAndDescriptionSection = () => (
    <div className={titleAndDescriptionContainer()}>
      <div className={title()}>
        <H3>{t("settings.integration.klaviyoKey.title")}</H3>
      </div>
      <P>{t("settings.integration.klaviyoKey.description")}</P>
    </div>
  );

  const renderKlaviyoApiKeyInput = () => (
    <Input
      label={t("settings.integration.klaviyoKey.inputLabel")}
      placeholder={t("settings.integration.klaviyoKey.inputPlaceholder")}
      type="klaviyoApiKey"
      role="textbox"
      {...register("klaviyoApiKey")}
      helperText={errors?.klaviyoApiKey?.message}
      variant={!!errors?.klaviyoApiKey?.message ? "error" : undefined}
      trailingSlot={renderTrailingSlotTooltip()}
      helperTextTestId="integration-klaviyo-api-input"
      testId="integration-klaviyo-api-input"
      id="integration-klaviyo-api-input"
    />
  );

  const renderButton = () => (
    <div className={buttonContainer()}>
      <Button
        type="submit"
        loading={isKlaviyoApiKeyPending || isPostKlaviyoPrivateKeyPending}
        trackingId="control-panel.collect.integration-settings.klaviyo-key.update"
        disabled={
          isKlaviyoApiKeyPending ||
          isPostKlaviyoPrivateKeyPending ||
          (lastKlaviyoApiKeyUpdate ?? klaviyoApiKey) === watch("klaviyoApiKey")
        }
        testId="integration-klaviyo-api-cta"
        id="integration-klaviyo-api-cta">
        {t("settings.integration.klaviyoKey.cta")}
      </Button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit(handleOnSubmitPostKlaviyoApiKey)}>
      {renderTitleAndDescriptionSection()}
      {renderKlaviyoApiKeyInput()}
      {renderButton()}
    </form>
  );
};

export default KlaviyoApiKeyForm;
